/**
 * Discord's "+ Add a Server" (Console v2 §3.1): Create My Own, then
 * Customize Your Server (an icon and "{name}'s server"), then Create, and
 * #general opens; or Join a Server with an invite link. Whether you may
 * create one is the console's (`community.server_creation`, worked out for
 * you as `can_create_guilds`): turned off, the dialog says what it said
 * before servers could be made; staff only, that it is limited for now and
 * an invite still works. The server checks it again either way.
 */
import { GUILD_NAME_MAX, GUILD_NAME_MIN, type Snowflake } from '@neosocial/protocol';
import { useRef, useState } from 'react';
import { plainText, tr, useT } from '../i18n';
import { serverOrigin } from '../lib/config';
import { blobToDataUri, IMAGE_ACCEPT, imageErrorText, looksLikeImage } from '../lib/image';
import { runAction } from '../mod/actions';
import { openInvite } from '../shell/InviteLink';
import { flagOn, useConfig } from '../store/config';
import { useData } from '../store/data';
import { goHub, useUi } from '../store/ui';
import { ImageEditor } from '../ui/ImageEditor';
import { Btn, Icon } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { serversApi } from './api';

type Step = 'start' | 'create' | 'join';

/**
 * What the console allows you now: `create`, or why not (`off` keeps the
 * old "not available yet" text, `limited` is staff only). No config yet
 * (it is loading, or a dev mock without one): the server decides.
 */
export function useCreation(): 'create' | 'off' | 'limited' {
  return useConfig((s) => {
    const c = s.config;
    if (!c) return 'create';
    if (c.can_create_guilds === true) return 'create';
    return c.settings['community.server_creation'] === 'off' ? 'off' : 'limited';
  });
}

/** An invite code from a link (`…/invite/CODE`, `?invite=CODE`) or the code itself. */
export function inviteCodeOf(text: string): string | null {
  const s = text.trim();
  const m =
    /\/invite\/([A-Za-z0-9-]{2,32})\/?(?:[?#].*)?$/.exec(s) ??
    /[?&]invite=([A-Za-z0-9-]{2,32})/.exec(s);
  if (m) return m[1]!;
  return /^[A-Za-z0-9-]{2,32}$/.test(s) ? s : null;
}

/** Resolves true once `pred` holds in the data store, false after `ms`. */
function whenData(pred: () => boolean, ms: number): Promise<boolean> {
  if (pred()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      resolve(pred());
    }, ms);
    const stop = useData.subscribe(() => {
      if (!pred()) return;
      clearTimeout(timer);
      stop();
      resolve(true);
    });
  });
}

/** After POST /guilds: its #general once GUILD_CREATE brought it. */
async function openCreated(guildId: Snowflake, general: Snowflake | null): Promise<void> {
  const ready = await whenData(
    () =>
      !!useData.getState().guilds[guildId] && (!general || !!useData.getState().channels[general]),
    8000,
  );
  useUi.getState().set({ guildId });
  if (ready && general) goHub({ kind: 'channel', channelId: general });
  else if (!ready) toast(tr().t('sv.make.slow'), { icon: 'clock', tone: 'mode' });
}

export function AddServer() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="rail-item">
      <button
        type="button"
        className="rail-btn ghost ns-add-server"
        aria-label={t('ns.addServer.label')}
        aria-haspopup="dialog"
        data-tip={t('ns.addServer.label')}
        data-tip-pos="end"
        onClick={() => setOpen(true)}
      >
        <Icon name="plus" size="l" />
      </button>
      {open ? <AddServerDialog onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

export function AddServerDialog({ onClose }: { onClose(): void }) {
  const [step, setStep] = useState<Step>('start');
  if (step === 'create') return <CreateStep onBack={() => setStep('start')} onClose={onClose} />;
  if (step === 'join') return <JoinStep onBack={() => setStep('start')} onClose={onClose} />;
  return <StartStep onPick={setStep} onClose={onClose} />;
}

function StartStep({ onPick, onClose }: { onPick(s: Step): void; onClose(): void }) {
  const { t } = useT();
  const creation = useCreation();
  return (
    <Dialog
      title={creation === 'create' ? t('sv.add.title') : t('ns.addServer.label')}
      icon="plus"
      tone="mode"
      cls="ns-add-server-dlg sv-add"
      onClose={onClose}
    >
      {creation === 'create' ? (
        <>
          <p className="ns-muted">{t('sv.add.body')}</p>
          <button
            type="button"
            className="sv-pick"
            data-pick="create"
            onClick={() => onPick('create')}
          >
            <span className="sv-pick-ic" aria-hidden="true">
              <Icon name="sparkles" />
            </span>
            <span className="sv-pick-t">
              <b>{t('sv.add.own')}</b>
              <span className="ns-muted">{t('sv.add.ownHint')}</span>
            </span>
            <Icon name="chev-right" cls="sv-pick-chev" />
          </button>
        </>
      ) : (
        <p className="ns-muted" data-creation={creation}>
          {t(creation === 'off' ? 'ns.addServer.body' : 'sv.add.limited')}
        </p>
      )}
      <div className="sv-invite">
        <h3>{t('sv.add.haveInvite')}</h3>
        <Btn kind="ghost" size="sm" icon="link" data-pick="join" onClick={() => onPick('join')}>
          {t('sv.add.join')}
        </Btn>
      </div>
    </Dialog>
  );
}

function CreateStep({ onBack, onClose }: { onBack(): void; onClose(): void }) {
  const { t } = useT();
  const me = useData((d) => d.me);
  // a field's value: without the direction marks a sentence puts around a name
  const [name, setName] = useState(() =>
    plainText(t('sv.make.defaultName', { name: me?.global_name || me?.username || '' })),
  );
  const [icon, setIcon] = useState<string | null>(null);
  const [editing, setEditing] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();
  const nameOk = trimmed.length >= GUILD_NAME_MIN && trimmed.length <= GUILD_NAME_MAX;
  const create = async () => {
    if (!nameOk || busy) return;
    setBusy(true);
    const r = await runAction(() => serversApi.create({ name: trimmed, icon }));
    if (!r.ok) {
      setBusy(false);
      return;
    }
    await openCreated(r.value.id, r.value.system_channel_id);
    setBusy(false);
    onClose();
  };
  return (
    <Dialog
      title={t('sv.make.title')}
      icon="sparkles"
      tone="mode"
      cls="ns-add-server-dlg sv-add sv-make"
      onClose={onClose}
      actions={
        <>
          <Btn kind="ghost" size="sm" icon="chev-left" onClick={onBack}>
            {t('sv.add.back')}
          </Btn>
          <Btn kind="primary" size="sm" busy={busy} onClick={() => void create()}>
            {t('sv.make.create')}
          </Btn>
        </>
      }
    >
      <p className="ns-muted">{t('sv.make.body')}</p>
      <div className="sv-make-icon">
        <button
          type="button"
          className="sv-upload"
          data-has-icon={icon ? '' : undefined}
          aria-label={t('sv.make.icon')}
          onClick={() =>
            // the console's Image uploads covers server icons too (the server refuses one then)
            flagOn('image_uploads')
              ? file.current?.click()
              : toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' })
          }
        >
          {icon ? (
            <img src={icon} alt="" />
          ) : (
            <>
              <Icon name="upload" />
              <span>{t('sv.make.upload')}</span>
            </>
          )}
        </button>
        {icon ? (
          <Btn kind="quiet" size="xs" onClick={() => setIcon(null)}>
            {t('sv.make.removeIcon')}
          </Btn>
        ) : null}
        <input
          ref={file}
          type="file"
          accept={IMAGE_ACCEPT}
          hidden
          data-make-file=""
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            if (!looksLikeImage(f)) {
              toast(t('img.err.unsupported'), { tone: 'warn', icon: 'image' });
              return;
            }
            setEditing(f);
          }}
        />
      </div>
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <label>
          <span className="ns-label">{t('sv.make.name')}</span>
          <input
            className="nx-input"
            dir="auto"
            value={name}
            maxLength={GUILD_NAME_MAX}
            data-autofocus
            autoComplete="off"
            aria-invalid={!nameOk || undefined}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {!nameOk ? <p className="ns-err">{t('sv.make.nameLength')}</p> : null}
      </form>
      {editing ? (
        <ImageEditor
          file={editing}
          purpose="icon"
          onCancel={() => setEditing(null)}
          onDone={(img) => {
            setEditing(null);
            blobToDataUri(img.blob).then(setIcon, (e: unknown) =>
              toast(imageErrorText(t, e), { tone: 'warn', icon: 'image' }),
            );
          }}
        />
      ) : null}
    </Dialog>
  );
}

function JoinStep({ onBack, onClose }: { onBack(): void; onClose(): void }) {
  const { t } = useT();
  const [text, setText] = useState('');
  const [bad, setBad] = useState(false);
  const join = () => {
    const code = inviteCodeOf(text);
    if (!code) {
      setBad(true);
      return;
    }
    onClose();
    void openInvite(code);
  };
  // the server's address (in the phone app the page's own origin is tauri://localhost)
  const origin = serverOrigin();
  return (
    <Dialog
      title={t('sv.join.title')}
      icon="link"
      tone="mode"
      cls="ns-add-server-dlg sv-add sv-join"
      onClose={onClose}
      actions={
        <>
          <Btn kind="ghost" size="sm" icon="chev-left" onClick={onBack}>
            {t('sv.add.back')}
          </Btn>
          <Btn kind="primary" size="sm" onClick={join}>
            {t('sv.join.go')}
          </Btn>
        </>
      }
    >
      <p className="ns-muted">{t('sv.join.body')}</p>
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          join();
        }}
      >
        <label>
          <span className="ns-label">{t('sv.join.label')}</span>
          <input
            className="nx-input"
            value={text}
            data-autofocus
            autoComplete="off"
            spellCheck={false}
            aria-invalid={bad || undefined}
            onChange={(e) => {
              setText(e.target.value);
              setBad(false);
            }}
          />
        </label>
        {bad ? (
          <p className="ns-err" role="alert">
            {t('sv.join.bad')}
          </p>
        ) : null}
        <p className="ns-muted sv-join-ex">
          {t('sv.join.examples', { code: 'hTKzmak', link: `${origin}/invite/hTKzmak` })}
        </p>
      </form>
    </Dialog>
  );
}
